/**
 * skaile preset — scaffold and validate preset YAML files.
 *
 * Subcommands: init, validate.
 * Delegates to @skaile/library preset module for parsing and validation.
 */

import { existsSync, readFileSync, writeFileSync } from "node:fs";
import path from "node:path";
import { Command } from "commander";
import { logErr, logInfo, logOk, logWarn, S } from "../helpers.ts";

/**
 * Creates the `skaile preset` command group.
 *
 * Scaffolds and validates preset YAML files used by the Asset Store v2.
 * Subcommands: `init [name]` (scaffold a `.preset.yaml` template) and
 * `validate <path>` (schema + cycle + nesting-depth check).
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/misc#preset
 */
export function makePresetCommand(): Command {
  const cmd = new Command("preset").description("Scaffold and validate preset YAML files");

  // ── preset init ─────────────────────────────────────────────────────────

  cmd
    .command("init [name]")
    .description("Scaffold a new .preset.yaml file")
    .option("--dir <path>", "Output directory", process.cwd())
    .action(async (name: string | undefined, opts: { dir: string }) => {
      const presetName = name ?? "my-preset";
      const filename = `${presetName}.preset.yaml`;
      const outPath = path.join(path.resolve(opts.dir), filename);

      if (existsSync(outPath)) {
        logErr(`File already exists: ${outPath}`);
        logInfo("Choose a different name or remove the existing file.");
        process.exit(1);
      }

      const template = generatePresetTemplate(presetName);
      writeFileSync(outPath, template, "utf-8");
      logOk(`Created ${S.brand(filename)}`);
      logInfo(`Path: ${outPath}`);
      logInfo("Edit the file to define your preset items, then validate with:");
      logInfo(`  skaile preset validate ${filename}`);
    });

  // ── preset validate ─────────────────────────────────────────────────────

  cmd
    .command("validate <path>")
    .description("Validate a preset YAML file (schema + cycles + nesting depth)")
    .action(async (filePath: string) => {
      await runPresetValidate(filePath);
    });

  return cmd;
}

// ── preset validate implementation ───────────────────────────────────────────

/**
 * Read + parse the preset file, exiting the process on read/parse failure.
 * Returns the validated preset manifest on success.
 */
async function loadPresetForValidation(resolved: string): Promise<any> {
  if (!existsSync(resolved)) {
    logErr(`File not found: ${resolved}`);
    process.exit(1);
  }

  const content = readFileSync(resolved, "utf-8");

  // Parse
  const { parsePresetYaml } = await import("@skaile/workspaces/library");

  const parseResult = parsePresetYaml(content);
  if (!parseResult.ok) {
    logErr("Preset validation failed (parse errors):");
    for (const err of parseResult.errors) {
      console.log(`  ${S.err("✗")} ${err}`);
    }
    process.exit(1);
  }

  // Note: ParseResult.preset is optional even when ok; on `ok: true` it's
  // always set by parsePreset(), but TypeScript can't infer that. Guard.
  if (!parseResult.preset) {
    logErr("Preset validation failed: parser returned no manifest");
    process.exit(1);
  }
  return parseResult.preset;
}

/**
 * Surface a count of nested preset refs. The full depth check runs at
 * apply-time via validateNestingDepth() inside applyPresetWithLibrary.
 */
async function reportNestedPresetRefs(preset: any): Promise<void> {
  // Check structural nesting: detectNestedPresetRefs requires a known-refs
  // set, which we don't have at structural-validation time. The full
  // depth check runs at apply-time via validateNestingDepth(presetRef,
  // currentDepth) inside applyPresetWithLibrary. Surface a count only.
  const { detectNestedPresetRefs } = await import("@skaile/workspaces/library");
  const nestedRefs = detectNestedPresetRefs(preset);
  if (nestedRefs.length > 0) {
    logInfo(`Nested preset refs: ${nestedRefs.length} (depth validated at apply-time)`);
  }
}

/**
 * Detect cycles in the preset's item-ref graph, exiting the process on a cycle.
 */
async function checkPresetCycles(preset: any, resolved: string): Promise<void> {
  // Check for cycle detection (requires resolving refs against library)
  // Phase 1: basic structural validation only. Full cycle detection
  // requires the discovery package's detectCycles() with a requires graph,
  // which needs the full Library context. We validate structure here.
  const { detectCycles } = await import("@skaile/workspaces/discovery");

  // Build a mini requires graph from the preset items
  const presetRef = path.basename(resolved, ".preset.yaml");
  const edges: Array<{ from: string; to: string; field: string }> = [];
  for (const item of preset.items) {
    if (item.ref) {
      edges.push({ from: presetRef, to: item.ref, field: "items[].ref" });
    }
  }

  if (edges.length === 0) return;

  const cycleResult = detectCycles(edges);
  if (!cycleResult.acyclic) {
    logErr("Cycle detected in preset references:");
    if (cycleResult.cyclePath) {
      console.log(`  ${S.err("✗")} ${cycleResult.cyclePath.join(" → ")}`);
    }
    process.exit(1);
  }
}

/**
 * Validate per-item placeholder structure (dry-run with no input values).
 * Returns true when any placeholder issues were surfaced as warnings.
 */
async function checkPresetPlaceholders(allPlaceholders: any[]): Promise<boolean> {
  if (allPlaceholders.length === 0) return false;

  const { validatePlaceholder } = await import("@skaile/workspaces/library");
  const invalidEntries: Array<{ key: string; errors: string[] }> = [];
  for (const ph of allPlaceholders) {
    // Validate with undefined value — checks structure only, not required-ness
    const result = validatePlaceholder(ph, ph.default);
    if (!result.valid) {
      invalidEntries.push({ key: ph.key, errors: result.errors });
    }
  }
  if (invalidEntries.length === 0) return false;

  logWarn("Placeholder issues:");
  for (const r of invalidEntries) {
    console.log(`  ${S.warn("!")} ${r.key}: ${r.errors.join(", ")}`);
  }
  return true;
}

/** Print the validation summary table + final pass/warn line. */
function printPresetSummary(preset: any, placeholderCount: number, hasWarnings: boolean): void {
  console.log();
  console.log(S.heading("  Preset Validation"));
  console.log(`  ${S.rule(40)}`);
  console.log(`  ${S.label("Name")}          ${preset.name}`);
  console.log(`  ${S.label("Version")}       ${preset.version ?? "unversioned"}`);
  console.log(`  ${S.label("Items")}         ${preset.items.length}`);
  console.log(`  ${S.label("Inputs")}         ${preset.inputs?.length ?? 0}`);
  console.log(`  ${S.label("Placeholders")}  ${placeholderCount}`);
  console.log();

  if (hasWarnings) {
    logWarn("Validation passed with warnings");
  } else {
    logOk("Preset is valid");
  }
}

/** Full `skaile preset validate <path>` pipeline. */
async function runPresetValidate(filePath: string): Promise<void> {
  const resolved = path.resolve(filePath);

  const preset = await loadPresetForValidation(resolved);

  await reportNestedPresetRefs(preset);
  await checkPresetCycles(preset, resolved);

  const allPlaceholders = preset.items.flatMap((item: any) => item.placeholders ?? []);
  const hasWarnings = await checkPresetPlaceholders(allPlaceholders);

  printPresetSummary(preset, allPlaceholders.length, hasWarnings);
}

// ── Template ────────────────────────────────────────────────────────────────

function generatePresetTemplate(name: string): string {
  return `# ${name}.preset.yaml
# Polymorphic Preset — composition + configuration primitive for the asset store.
# See: _devlog/specs/2026-05-03-ai-asset-store-v2-design.md § Presets

name: "${name}"
version: "1.0.0"
description: "TODO: describe what this preset configures"

# Placeholders — values the user provides at apply-time.
# Types: text, multiline, secret, number, boolean, enum, oauth, instance-picker
placeholders:
  - key: EXAMPLE_KEY
    type: text
    label: "Example placeholder"
    required: true
    # default: "some-value"
    # hint: "Help text shown to the user"

# Items — each becomes a Library Instance when the preset is applied.
# Ref types:
#   - Asset ref (kind:publisher/name@pin) → creates new Instance
#   - Library Instance ref (#uuid) → reuses existing Instance
#   - Nested Preset ref (preset:path.preset.yaml) → recursive apply (max depth 3)
items:
  - id: example-item
    ref: "skill:@skaile/example-skill@^1.0"
    config:
      # Use \${INPUT:EXAMPLE_KEY} for placeholder interpolation
      setting: "\${INPUT:EXAMPLE_KEY}"
    # credential_ref: "\${SECRET:EXAMPLE_KEY}"  # for secret placeholders
`;
}
